import React from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import PaperFragment from "../../../../helper-components/misc/PaperFragment";
import StackedHeader from "../../../../helper-components/misc/StackedHeader";
import DataGridTable from "../../../../helper-components/table/DataGridTable";

import CookieAddForm from "./CookieAddForm";
import CookieEditForm from "./CookieEditForm";

import { createUploadObject } from "../../../../helper-functions/shared/miscUtils";
import { columns, defaultTableOptions } from "../../../../helper-functions/table/cookies";
import { createCookie, deleteCookies, queryCookies, updateCookie } from "../../../../helper-functions/api/cookieQueries";

import { getLanguage } from "../../../../config/language/language";

/**
 * Table showing cookies.
 * If no data found, show error message instead.
 */
export default function CookiesTable(props) {
    const cookies = props.backend.cookies;

    const showCreateDialog = () => {
        props.showDialog({
            title: "Add new cookie",
            message: `Streaming platforms can sometimes flag suspicious IPs and might require authorization to view content. This is common when dealing with public hosting providers or VPNs.

            This can often be resolved by providing valid cookies along with the request.`,
            component: <CookieAddForm {...props} callback={(confirm, options) => handleCreate(confirm, options)} />
        });
    };

    const showDeleteDialog = () => {
        props.showDialog({
            title: "Delete cookies(s)",
            message: `Are you sure you want to delete the ${cookies.selected.length} selected cookie(s)?

            This action cannot be undone.`,
            actionTitle: "Delete",
            actionColor: "error",
            callback: (confirm) => handleDelete(confirm)
        });
    };

    const showUpdateDialog = () => {
        props.showDialog({
            title: "Edit cookie",
            message: "Click 'Update' to confirm changes.",
            component: <CookieEditForm {...props} row={cookies.selected?.[0]} callback={(confirm, options) => handleUpdate(confirm, options)} />
        });
    };

    const handleCreate = (confirm, options = {}) => {
        props.setOpenDialog(false); // Manually close the dialog.
        if (!confirm) return;

        const { row } = options;
        const body = createUploadObject(row, ["form"]);

        createCookie(props, { body }, (response) => {
            if (!response.manual_use) return;
            props.showSnackbar({ color: "info", message: "Cookie will be used for all subsequent queries, regardless of timestamp" });
        });
    };

    const handleDelete = (confirm) => {
        if (!confirm) return;

        const ids = cookies.selected.map((q) => q.id);
        deleteCookies(props, { body: { ids }, ids });
    };

    const handleUpdate = (confirm, options = {}) => {
        props.setOpenDialog(false); // Manually close the dialog.
        if (!confirm) return;

        const { row } = options;
        const body = createUploadObject(row, ["form"]);

        updateCookie(props, { body, id: row.id }, (response) => {
            if (!response.manual_use) return;
            props.showSnackbar({ color: "info", message: "Cookie will be used for all subsequent queries, regardless of timestamp" });
        });
    };

    return (
        <PaperFragment
            title={
                <StackedHeader
                    {...props}
                    title="Cookies"
                    subtitle="View and manage cookies."
                    icons={[
                        {
                            icon: <DeleteIcon />,
                            tooltip: "Delete selected cookies",
                            disabled: cookies.selected.length < 1,
                            onClick: showDeleteDialog
                        },
                        {
                            icon: <EditIcon />,
                            tooltip: "Edit selected cookie",
                            onClick: showUpdateDialog,
                            disabled: cookies.selected.length !== 1
                        }
                    ]}
                    buttons={[
                        {
                            icon: <AddIcon />,
                            title: "New cookie",
                            onClick: showCreateDialog
                        }
                    ]}
                />
            }
            value={
                <DataGridTable
                    {...props}
                    {...defaultTableOptions}
                    rowHeight={60}
                    states={cookies}
                    columns={columns(props)}
                    query={(options) => queryCookies(props, options)}
                    empty={getLanguage().NO_COOKIES_FOUND}
                    refresh={10}
                />
            }
        />
    );
}
